import { useState } from "react"
import PropTypes from "prop-types"
import { Table, ComboBox, DropdownMenu } from "components/ui"
import { Button, StackView } from "@planningcenter/tapestry-react"
import AreaForm from "./AreaForm"
import AreaAttendeeDropdown from "./AreaAttendeeDropdown"
import { isAreaAttendeeDisabled, areaRestrictionsToString } from "./areaRestriction"
import sortBy from "utils/sorter"
import { Router, navigate } from "@reach/router"
import { colors } from "components/shared/admin_colors"
import { isBlank } from "utils/helpers"
import { confirmableAssignmentUpdate } from "./confirmableAssignmentUpdate"
import useScreenWidth from "hooks/use_screen_width"

const Areas = ({
  areaId,
  allAttendeesPath,
  onAttendeeAssign,
  onAttendeeUnassign,
  onUnassignAllAlert,
  areas,
  attendees,
  onAreaUpdate,
  gradeOptions,
  genderOptions,
  eventStartDate,
  basePath,
  signupFeatures
}) => {
  const area = areas[Number(areaId)]
  const [error, setError] = useState(null)

  if (isBlank(area)) {
    window.location = allAttendeesPath
    return null
  }

  const { screenWidth, BREAKPOINTS } = useScreenWidth()

  const handleViewRegistration = attendee => {
    navigate(attendee.registrationPath)
  }

  const handleUnassignAll = (assignedAttendees, area) =>
    onUnassignAllAlert(() => onAttendeeUnassign(assignedAttendees, area))

  const renderActionsArea = (row, _col) => (
    <StackView spacing={1} axis="horizontal">
      <Button
        theme="destroy"
        icon={{ name: "general.outlinedMinusCircle" }}
        tooltip={{ title: "Unassign" }}
        variant="naked"
        onClick={() => onAttendeeUnassign([row], area)}
      />
      <Button
        icon={{ name: "general.newWindow" }}
        tooltip={{ title: "View Registration" }}
        variant="naked"
        onClick={() => handleViewRegistration(row)}
      />
    </StackView>
  )

  const columns = [
    { cell: "firstName", label: "First Name", isSortable: true },
    { cell: "lastName", label: "Last Name", isSortable: true },
    { cell: "type", label: "Selection", isSortable: true },
    {
      cell: "signupTime.contextualDateTime",
      label: "For Date",
      isSortable: true,
      isShown: signupFeatures.includes("separate_times")
    },
    {
      cell: "ageToS",
      label: "Age",
      isSortable: true,
      isShow: signupFeatures.includes("attendees")
    },
    {
      cell: "actions",
      label: "Area actions",
      hideHeaderLabel: true,
      style: { textAlign: "right" },
      render: renderActionsArea
    }
  ]

  const handleSave = params => onAreaUpdate(area, params)

  const handleAttendeeSelect = (selectedAttendee, handleClear) => {
    if (!selectedAttendee) {
      return
    }

    confirmableAssignmentUpdate({
      area,
      attendee: selectedAttendee,
      onConfirm: () =>
        onAttendeeAssign(selectedAttendee, area)
          .then(_ => handleClear())
          .catch(error => setError({ id: selectedAttendee.id, message: error.message })),
      onCancel: () => {}
    })
  }

  const assignedAttendees = sortBy(
    "name",
    attendees.filter(aa => aa.assignedArea && Number(aa.assignedArea.id) === Number(area.id))
  )

  const unassignedAttendees = sortBy(
    "name",
    attendees
      .filter(aa => !aa.assignedArea || Number(aa.assignedArea.id) !== Number(area.id))
      .map(attendee => {
        const isDisabled = isAreaAttendeeDisabled(area, attendee)

        return {
          ...attendee,
          isDisabled,
          key: attendee.id,
          // eslint-disable-next-line react/display-name
          render: menuItem => <AreaAttendeeDropdown showRestrictions={false} menuItem={menuItem} />
        }
      })
  )

  const renderSubText = () => {
    return (
      <>
        <div>
          {areaRestrictionsToString(area, signupFeatures).map(r => (
            <span key={r.label} className="mr-1">
              <strong style={{ paddingRight: "0.2rem", fontWeight: "600" }}>{r.label}</strong>
              {r.value}
            </span>
          ))}
        </div>
        {error && <div style={{ color: colors.error }}>Assignment Failed: {error.message}</div>}
      </>
    )
  }

  const areaPath = `${basePath}/${areaId}`

  let actionMenuItems = [{ text: "Edit area settings", linkTo: `${areaPath}/edit` }]
  if (assignedAttendees.length > 0) {
    actionMenuItems = [
      ...actionMenuItems,
      { text: "Unassign all", onClick: () => handleUnassignAll(assignedAttendees, area) }
    ]
  }

  return (
    <Router primary={false}>
      <AreaForm
        path="edit"
        eventStartDate={eventStartDate}
        area={area}
        onCancel={() => navigate(-1)}
        onSave={handleSave}
        gradeOptions={gradeOptions}
        genderOptions={genderOptions}
        signupFeatures={signupFeatures}
      />

      <Table
        path="/"
        heading={{
          headerText: area.name,
          headerSubText: renderSubText(),
          style: { flexWrap: screenWidth < BREAKPOINTS.LG ? "wrap" : "nowrap" }
        }}
        headingChildren={
          <div className="d-f@sm ai-c mt-1 mt-0@md jc-fe ml-1 g-1">
            <DropdownMenu text="Actions" items={actionMenuItems} />
            <div style={{ width: "248px" }}>
              <ComboBox
                disabled={area.capacityRemaining === 0}
                placeholder={
                  area.capacityRemaining === 0
                    ? "Area is currently full"
                    : "Search for someone to add..."
                }
                items={sortBy("isDisabled", unassignedAttendees, "desc")}
                itemKey="name"
                onSelect={handleAttendeeSelect}
              />
            </div>
          </div>
        }
        columns={columns}
        rows={assignedAttendees}
        style={{ marginBottom: 8 }}
        emptyTableMessage="No attendees in this area"
        defaultSort="firstName"
      />
    </Router>
  )
}

Areas.propTypes = {
  allAttendeesPath: PropTypes.string,
  assignmentTypeId: PropTypes.string,
  basePath: PropTypes.string,
  areaId: PropTypes.string,
  areas: PropTypes.shape({}),
  attendees: PropTypes.array,
  onAttendeeAssign: PropTypes.func,
  onAttendeeUnassign: PropTypes.func,
  onUnassignAllAlert: PropTypes.func,
  onAreaUpdate: PropTypes.func,
  gradeOptions: PropTypes.shape({}),
  genderOptions: PropTypes.shape({}),
  eventStartDate: PropTypes.string,
  signupFeatures: PropTypes.array
}

export default Areas
